import { useCallback, useEffect, useState } from 'react'
import {
  admin, ApiError, endpoints, Unauthorized,
  type Account, type AgendaItem, type BoardMember, type Commitment,
  type Meeting, type NextMeeting,
} from './api/client'
import { Shell, type Screen } from './components/Shell'
import { Commitments, Meetings, TheTable } from './screens/Screens'
import { Home } from './screens/home/Home'
import { BoardConfig } from './screens/BoardEditor'
import { Capture, ConsultAnswer } from './screens/Capture'
import { CompanySettings } from './screens/Company'
import { Settings } from './screens/Settings'
import { Cabinet } from './screens/Cabinet'
import { Hub } from './screens/Hub'
import { TheNumbers } from './screens/Numbers'
import { Landing } from './screens/landing/Landing'
import { Why } from './screens/landing/Why'
import { AppSignIn, JoinBoard, ResetPassword, VerifyEmail } from './screens/SignIn'
import { Admin } from './screens/admin/Admin'
import { MeetingMinutes } from './screens/Minutes'
import { LiveMeeting } from './screens/Live'
import { TradeContext } from './trade'
import { BillingCard, Paywall, TrialBanner, useBilling } from './screens/Billing'
import { Writing } from './screens/landing/Blog'
import { WRITING_PUBLIC } from './screens/landing/writing'
import { RequestWidget } from './components/RequestWidget'
import { isNativeApp } from './native'
import { Onboarding } from './screens/Onboarding'
import { toTop } from './scroll'

export default function App() {
  /*
    /admin is the operator's, not a tenant's. It has its own cookie and its own password,
    so it is decided before the tenant session is even looked at: an owner signed in to
    their board has no business here, and an admin has no board.
  */
  const path = window.location.pathname.replace(/\/+$/, '')

  if (path === '/admin') return <Admin />

  // The far end of a password reset email. Above the session check on purpose:
  // somebody who still holds a valid cookie on another device should still be able to
  // use the link, and handing them a dashboard would look like the link had failed.
  if (path === '/reset') return <ResetPassword />

  // Same reasoning: reached from an email, and it has to work whatever session the
  // browser happens to be holding.
  if (path === '/verify') return <VerifyEmail />

  // An invitation to somebody else's board. Above the session check for the same
  // reason as the others: it arrives by email and must work whatever cookie is held.
  if (path === '/join') return <JoinBoard />

  // Published writing is public. Routed here rather than inside Landing so that an owner
  // who is signed in still reads the article instead of being handed their dashboard.
  return (
    <>
      {path === '/why' ? <Why />
        : WRITING_PUBLIC && path === '/blog' ? <Writing slug={null} />
        : WRITING_PUBLIC && path.startsWith('/blog/') ? <Writing slug={decodeURIComponent(path.slice(6))} />
        : <Product />}
      <AdminOverlay />
    </>
  )
}

/*
  The page request widget, shown to whoever is holding an admin cookie and nobody else.

  One cheap call on boot decides it. Mounted at the root so it rides over the homepage, an
  article and the signed-in dashboard alike: a request can be left on any of them, and the
  path it records is whatever the address bar says.
*/
function AdminOverlay() {
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    void admin.me().then(() => setIsAdmin(true)).catch(() => setIsAdmin(false))
  }, [])

  return isAdmin ? <RequestWidget /> : null
}

function Product() {
  const [account, setAccount] = useState<Account | null>(null)
  const [checking, setChecking] = useState(true)
  const [screen, setScreen] = useState<Screen>('home')

  // Which meeting the ledger has been asked to open. Held here rather than in the Meetings
  // screen so leaving the tab and coming back lands on the list, not on last week's minutes.
  const [openMeeting, setOpenMeeting] = useState<string | null>(null)

  /*
    Whether that meeting is being watched live. Decided when it is opened rather than read
    from status on every render: a meeting that completes while somebody is reading the
    closing recommendation would otherwise have the screen pulled out from under them.
  */
  const [watchingLive, setWatchingLive] = useState(false)

  /*
    The item "Answer it" on the meeting card is sending the owner to. Cleared by any
    ordinary navigation, so returning to The Table later does not keep forcing the same
    question open.
  */
  const [focusItem, setFocusItem] = useState<string | null>(null)

  /** Ordinary navigation. Drops any focused item on the way. */
  const go = (s: Screen) => { setFocusItem(null); setScreen(s) }

  /*
    Straight to one item on The Table, opened.

    The same path "Answer it" on the meeting card already takes, which is the point: Home
    showed the item and then made the owner find it again on the next screen. TheTable
    opens whatever focusId names and scrolls it into view, so this only has to name it.
  */
  const openTableItem = (id: string) => { setFocusItem(id); setScreen('table') }

  const [next, setNext] = useState<NextMeeting | null>(null)
  const [inbox, setInbox] = useState<AgendaItem[]>([])
  const [commitments, setCommitments] = useState<Commitment[]>([])
  const [board, setBoard] = useState<BoardMember[]>([])
  const [meetings, setMeetings] = useState<Meeting[]>([])

  const [capturing, setCapturing] = useState(false)
  const [consultId, setConsultId] = useState<string | null>(null)
  const [toast, setToast] = useState<{
    message: string
    undo?: () => void
    /** A second, quieter button. Used to walk the owner to what just changed. */
    go?: { label: string; run: () => void }
  } | null>(null)

  /*
    A commitment worth pointing at, cleared as soon as it has been.

    Accepting a board member's ask writes a dated commitment and says so in a toast, and
    that used to be the end of it. On an account carrying thirteen open actions the new one
    lands last in its section, under everything already late, and the Home widget shows the
    three soonest, so the one thing the owner just did was the one thing they could not see.
  */
  const [highlight, setHighlight] = useState<string | null>(null)

  // Stable, or the effect that clears it re-arms on every render of the screen.
  const clearHighlight = useCallback(() => setHighlight(null), [])

  // Set when the API answers 402. The wall is the server's decision, not this component's.
  const [paywall, setPaywall] = useState<string | null>(null)
  const { billing } = useBilling()

  // A 401 anywhere means the session ended. Dropping to the sign-in screen is the honest
  // response; showing an error would ask the owner to diagnose it.
  const guard = useCallback(async (work: () => Promise<void>) => {
    try {
      await work()
    } catch (e) {
      if (e instanceof Unauthorized) setAccount(null)
      // 402 is the wall. Every tenant route returns it, so there is nothing behind here
      // left to render and the reason the server gave is the one shown.
      else if (e instanceof ApiError && e.status === 402) setPaywall(e.message)
      else console.error(e)
    }
  }, [])

  const refresh = useCallback(async () => {
    await guard(async () => {
      const [n, i, c, b, m] = await Promise.all([
        endpoints.nextMeeting(),
        endpoints.inbox(),
        endpoints.commitments(),
        endpoints.board(),
        endpoints.meetings(),
      ])
      setNext(n); setInbox(i); setCommitments(c); setBoard(b); setMeetings(m)
    })
  }, [guard])

  useEffect(() => {
    void (async () => {
      try {
        setAccount(await endpoints.me())
      } catch {
        setAccount(null)
      } finally {
        setChecking(false)
      }
    })()
  }, [])

  useEffect(() => { if (account) void refresh() }, [account, refresh])

  // The chip has to move on its own while the worker preps items, or §3's "visibly fills"
  // is only true if you reload.
  useEffect(() => {
    if (!account) return
    const id = setInterval(() => { void refresh() }, 15_000)
    return () => clearInterval(id)
  }, [account, refresh])

  /*
    A new screen starts at the top of itself.

    Nothing navigates here, so without this the scroll position carries over from whatever
    the last screen left it at. Skipped when an item was focused, because that item scrolls
    itself into view (TableItem) and child effects run before their parent's: resetting
    afterwards would throw away the scroll the owner actually asked for.
  */
  useEffect(() => {
    if (focusItem) return
    toTop()
  }, [screen, focusItem])

  useEffect(() => {
    if (!toast) return
    const id = setTimeout(() => setToast(null), 6000)
    return () => clearTimeout(id)
  }, [toast])

  if (checking) return <div className="signin"><div className="skeleton" style={{ width: 180 }} /></div>
  /*
    The landing page is the website's front door and the app has no use for it: it sells,
    and an app that sells outside its own store is an app that gets pulled (3.1.3(f)). The
    app opens on the sign-in card instead.
  */
  if (!account) {
    return isNativeApp()
      ? <AppSignIn onSignedIn={setAccount} />
      : <Landing onSignedIn={setAccount} />
  }

  // Signed in, but not entitled. Shown instead of the product rather than over it: the API
  // is answering 402 to everything behind this, so a dimmed dashboard would be a lie.
  if (paywall) return <Paywall reason={paywall} />

  /*
    Never been walked through setup, so walk them through it.

    After the paywall on purpose. Somebody whose subscription has lapsed does not need a
    tour of a product they cannot use, and the wizard writes to endpoints the paywall is
    answering 402 to anyway.

    Before the shell, not inside it: this is the only screen in the product with no
    navigation on it, because an owner being asked six questions should not also be offered
    nine places to go instead, and all nine are empty until this is done.
  */
  if (!account.onboardedAt) {
    return (
      <Onboarding
        onDone={() => setAccount(a => (a ? { ...a, onboardedAt: new Date().toISOString() } : a))}
      />
    )
  }

  // documentIds are files already uploaded and not yet attached to anything. Optional, so
  // every caller that has nothing to attach is unchanged.
  const capture = async (text: string, documentIds?: string[]) => {
    await guard(async () => {
      const { id } = await endpoints.capture(text, documentIds)
      setToast({
        message: 'Added to Friday',
        undo: async () => {
          await endpoints.patchItem(id, 'delete')
          setToast(null)
          await refresh()
        },
      })
      await refresh()
    })
  }

  const answer = async (id: string, value: string) => {
    await guard(async () => { await endpoints.answer(id, value); await refresh() })
  }

  const patch = async (id: string, action: 'defer' | 'delete') => {
    await guard(async () => {
      await endpoints.patchItem(id, action)
      setToast({
        message: action === 'defer' ? 'Moved off this week' : 'Removed',
        undo: async () => {
          await endpoints.patchItem(id, 'restore')
          setToast(null)
          await refresh()
        },
      })
      await refresh()
    })
  }

  const resolve = async (id: string, status: string, note?: string) => {
    await guard(async () => { await endpoints.resolve(id, status, note); await refresh() })
  }

  return (
    <TradeContext.Provider value={account.trade}>
      <Shell
        screen={screen}
        onNavigate={go}
        onCapture={() => setCapturing(true)}
        onSignOut={async () => { await endpoints.logout(); setAccount(null) }}
        next={next}
        inboxCount={inbox.length}
        company={account.company}
        /*
          Meetings is three screens behind one id. The bar has to say which, and back has
          to return to the ledger rather than to the tab root, or reading last month's
          minutes becomes a one-way trip.
        */
        title={screen === 'meetings' && openMeeting
          ? (watchingLive ? 'In session' : 'Minutes')
          : null}
        onBack={screen === 'meetings' && openMeeting
          ? () => { setOpenMeeting(null); setWatchingLive(false); void refresh() }
          : null}
      >
        {/* Only speaks in the last few days of a trial. A countdown from day one is a
            nag; the day before the card is charged is information. */}
        <TrialBanner billing={billing} />

        {screen === 'home' && (
          <Home
            company={account.company}
            next={next} inbox={inbox} commitments={commitments} board={board}
            onNavigate={go}
            onAsked={setConsultId}
            onCaptured={capture}
            onResolve={resolve}
            onCapture={() => setCapturing(true)}
            onOpenSeat={() => setScreen('board')}
            onOpenItem={openTableItem}
          />
        )}
        {screen === 'table'       && <TheTable items={inbox} board={board} onPatch={patch} onAnswer={answer} focusId={focusItem} />}
        {screen === 'meetings'    && (openMeeting
          ? (watchingLive
              ? <LiveMeeting
                  meetingId={openMeeting}
                  onBack={() => { setOpenMeeting(null); setWatchingLive(false); void refresh() }}
                  onOpenMinutes={() => { setWatchingLive(false); void refresh() }}
                />
              : <MeetingMinutes meetingId={openMeeting} onBack={() => setOpenMeeting(null)} />)
          : <Meetings
              meetings={meetings}
              next={next}
              onOpen={(id, live) => { setOpenMeeting(id); setWatchingLive(live) }}
              onAnswerWaiting={() => {
                // The first question still waiting. There is rarely more than one, and
                // the rest are one screen away once this lands on The Table.
                const waiting = inbox.find(i => i.clarifierQuestion && !i.clarifierAnswer)
                if (waiting) setFocusItem(waiting.id)
                setScreen('table')
              }}
            />)}
        {screen === 'commitments' && (
          <Commitments
            commitments={commitments}
            highlight={highlight}
            onHighlighted={clearHighlight}
            onResolve={resolve}
            onAnswered={refresh}
          />
        )}
        {screen === 'cabinet'     && <Cabinet />}
        {screen === 'numbers'     && <TheNumbers />}
        {screen === 'board'       && <BoardConfig members={board} onSaved={refresh} />}
        {screen === 'company'     && <CompanySettings onSaved={refresh} />}
        {screen === 'settings'    && <Settings account={account} extra={<BillingCard />} />}
        {screen === 'hub'         && (
          <Hub
            company={account.company}
            board={board}
            onNavigate={go}
            onSignOut={async () => { await endpoints.logout(); setAccount(null) }}
          />
        )}
      </Shell>

      {capturing && (
        <Capture
          onClose={() => setCapturing(false)}
          onCaptured={capture}
          onAsked={setConsultId}
          board={board}
        />
      )}

      {consultId && (
        <ConsultAnswer
          consultId={consultId}
          board={board}
          onClose={() => setConsultId(null)}
          onEscalated={() => { setToast({ message: 'Added to Friday' }); void refresh() }}
          onCommitted={(message, undo, commitmentId) => {
            setToast({
              message,
              undo: async () => { await undo(); setToast(null); await refresh() },
              go: commitmentId
                ? {
                    label: 'See it',
                    run: () => {
                      setConsultId(null)
                      setHighlight(commitmentId)
                      go('commitments')
                      setToast(null)
                    },
                  }
                : undefined,
            })
            void refresh()
          }}
        />
      )}

      {toast && (
        <div className="toast" role="status">
          <span>{toast.message}</span>
          {toast.go && (
            <button className="toast__action" onClick={toast.go.run}>{toast.go.label}</button>
          )}
          {toast.undo && <button className="toast__action" onClick={toast.undo}>Undo</button>}
        </div>
      )}
    </TradeContext.Provider>
  )
}
